/**
 * Page copy for /services/intelligent-quality-operations.
 *
 * Source: page-intelligent-quality-operations.md, supplied by the client. H1 and
 * page match; the source specifies the superseded v1 slug
 * /services/managed-qa-services, and the built route is used instead.
 *
 * Links remapped to real routes where they exist; /ai-maturity-model and
 * /ai-native-delivery-framework are not built and sit at "#".
 *
 * **THE CLUTCH BLOCK IS REMOVED** — client's decision, 2026-08-19. The source
 * put one after §6, on the reasoning that "the business case is where an
 * external voice does the most work". It went through three shapes on the way
 * out: the approved 5.0 stat plus testimonial, then that alongside Clutch's own
 * type 12 review widget, then the widget alone, then nothing. **This page now
 * loads no third-party script at all**, and carries no social proof — the
 * homepage stat card and /about are where the Clutch rating lives.
 *
 * **§7, THE CASE STUDY, IS REMOVED** — client's decision, 2026-08-19. It was
 * "AI Delivery Reporting" and it carried three problems at once: the 90% / 100%
 * / 75% figures, which come from the five case studies the client rejected and
 * are marked under review in CLAUDE.md; an unfilled client-shape gap; and a
 * "Read the full case study" link pointing at a page that does not exist. The
 * copy is recoverable from git.
 *
 * GAPS CLOSED 2026-08-19: fractional days per month (§3, now 10–20 days a
 * month), notice period (§4, now 30 days), and §8, which will carry no price at
 * all — see the note above `pricing`.
 *
 * NO PLACEHOLDERS LEFT ON THIS PAGE. The last was the §6 cost framing, removed
 * at the client's request; the research for it is kept beside `businessCase`.
 */

export const seo = {
  title: "Managed QA Services & Intelligent Quality Operations",
  description:
    "Your quality function, run by a small senior team: fractional Head of QA, architects, analysts and automation engineers. Monthly, rolling, no minimum term.",
} as const;

export const hero = {
  opening: [
    'Quality is currently your problem. Not because you want to own it — because there\'s nobody else senior enough to. So release decisions come to you, the test suite has no owner, and "are we ready to ship" is a judgement call you make with incomplete information.',
    "This is the service that takes it off your desk. Not testers to manage. A quality function that runs and reports to you.",
  ],
} as const;

export const hiring = {
  heading: "Should I hire a Head of QA or outsource it?",
  lead: "Most CTOs in this position have already tried to hire their way out. The reasons it stalls are consistent:",
  reasons: [
    {
      lead: "The search runs three to six months",
      tail: ", and that's when it works. A Head of QA search that fails costs two quarters and you're back where you started.",
    },
    {
      lead: "You can't justify a full-time senior hire",
      tail: " at your headcount, but you need senior judgement — so the role gets pushed down to someone who can't set standards for engineers above their grade.",
    },
    {
      lead: "You've been burned by a bad hire.",
      tail: " Quality leadership is hard to interview for, and the failure only becomes visible six months in.",
    },
    {
      lead: "You'd need to hire more than one person.",
      tail: " A Head of QA with nobody to direct is an expensive consultant with a title.",
    },
  ],
  close:
    "You don't need to win a hiring race for roles the whole market is competing over. You need the function to work.",
} as const;

export const team = {
  heading: "What runs your quality function",
  lead: "We assemble a small team around what you actually need. Not everyone below is on every engagement.",
  lead_role: {
    title: "Fractional Head of QA",
    icon: "users",
    body: "Owns quality as a function. Sets the standard, runs the process, makes the release calls, and reports to you in terms you can act on. Part-time by design — most mid-market companies need senior judgement continuously and senior hours occasionally.",
    emphasis: "This is the role that changes your week. Everything else supports it.",
    /**
     * Client-supplied 2026-08-19. The source called this "the first thing a CTO
     * will ask", which is why it renders as a figure rather than a line of body
     * copy — it is the number that makes "fractional" mean something.
     *
     * En dash, not the hyphen it was sent as: the site sets ranges that way
     * throughout ("2–3 hours"). Typography, not a copy change.
     */
    commitment: {
      label: "Typical commitment",
      value: "10–20 days",
      unit: "a month",
    },
  },
  roles: [
    {
      title: "Quality Architect",
      icon: "cube",
      body: "Designs the approach. Test strategy, coverage architecture, tooling decisions, and the conventions that keep the suite maintainable as the team grows.",
    },
    {
      title: "QA Analyst",
      icon: "search",
      body: "The thinking work ahead of the testing. Requirement analysis, risk assessment, test design, and acceptance criteria that don't produce arguments in refinement.",
    },
    {
      title: "AQA Professional",
      icon: "gears",
      body: "Builds and maintains the automation. Suite development, pipeline integration, flake reduction, and the day-to-day work of keeping the tests trustworthy.",
    },
  ],
} as const;

export const howItWorks = {
  heading: "How it works",
  points: [
    {
      lead: "You approve the people before anything starts.",
      tail: " You interview your fractional Head of QA. If they're not right, you meet someone else. This is the opposite of how staffing works, where you're assigned whoever is available and find out in week three.",
    },
    {
      lead: "Month one is concrete.",
      tail: " By the end of it you have a current-state picture, a quality standard written down, and release reporting you didn't have before. Not onboarding — output.",
    },
    {
      lead: "Monthly, rolling, no minimum term.",
      tail: " Most competitors want six months up front. If the function isn't working, you leave. That's a deliberate transfer of risk from you to us, and it's why month one matters.",
    },
  ],
  terms: [
    { label: "Commitment", value: "Monthly, rolling" },
    { label: "Minimum term", value: "None" },
    { label: "Pricing", value: "Per engagement, roles included" },
    /** Client-supplied 2026-08-19, confirming the source's own guess. */
    { label: "Notice", value: "30 days" },
  ],
} as const;

export const smallTeam = {
  heading: "How big should a QA team be?",
  body: [
    "We work with a small number of clients at a time, with a small senior team. No juniors on client work.",
    "That's a constraint and we'd rather state it than have you discover it. It means we can't scale you from four engineers to forty inside a quarter. It also means you get the people you interviewed, on your account, for the length of the engagement — rather than a senior in the pitch and a junior in the sprint.",
  ],
  close:
    "If you need volume capacity, we're the wrong choice and we'll say so on the first call.",
} as const;

/**
 * §6.
 *
 * The source compares against a single full-time hire, written when the
 * Fractional Head of QA was the headline role and the rest "supported it". The
 * client has since confirmed all four roles are core and equally weighted, which
 * makes one hire the wrong baseline — it understates the service and leaves two
 * rows factually wrong ("3–6 months search" is one search; "two quarters lost"
 * is one bad hire).
 *
 * Rebased against building the function in-house, at the client's direction.
 *
 * NEW COPY, pending sign-off: the left column header and the left cells of every
 * row except "Scales down". Unchanged from the source: the right column, the
 * row labels, the heading, the lead and the risk paragraph.
 *
 * The "Risk if wrong" right cell had "30 days' notice" removed while §4's notice
 * period was unknown, so it claimed only what §4 could support. The client
 * confirmed 30 days on 2026-08-19 and the notice is back — this is the row where
 * it does the most work, because the whole cell is about how cheaply you exit.
 * The wording is reconstructed rather than restored from the source, so treat it
 * as NEW COPY pending sign-off.
 */
export const businessCase = {
  heading: "The business case",
  lead: "Written for the conversation with your CEO or CFO.",
  columns: ["", "Building the team in-house", "Intelligent Quality Operations"],
  rows: [
    [
      "Time to productive",
      "Four roles to fill. Three to six months per search, then ramp",
      "Weeks",
    ],
    [
      "Cost",
      "Four salaries + benefits + recruitment fees + equity",
      "Monthly fee, no employment cost",
    ],
    [
      "Risk if wrong",
      "Termination, re-hire, two quarters lost — for each role that misses",
      "Stop the engagement on 30 days' notice. No termination, no re-hire",
    ],
    [
      "Coverage",
      "Complete only once every role is hired",
      "Leadership plus the disciplines around it, from the start",
    ],
    ["Scales down", "No", "Yes"],
  ],
  riskLead: "The risk side.",
  riskBody:
    " A defect that reaches customers in a regulated environment costs more than a year of this engagement. So does a release date missed twice in front of a board. Those are the numbers this service is actually competing against — not a salary line.",
} as const;

/**
 * §6 COST RESEARCH — NOT ON THE PAGE, AND NO LONGER MARKED AS MISSING.
 *
 * The placeholder asking the client for their own cost framing was removed at
 * their request, so §6's "Cost" row stays as the source wrote it: "Four
 * salaries + benefits + recruitment fees + equity". That is a structural claim
 * with no figures behind it, which is defensible — but it is now a settled
 * state rather than a gap waiting to be filled, and nothing on the page says so.
 *
 * The research is kept because the question will come back the moment anyone
 * asks "can we put a number on this", and it took a morning to assemble.
 *
   * 2026 base salaries, the client's three markets:
   *   Head of QA      US $189-205k (Director of QA $268k) · UK £77k · IE €77-88k
   *   Quality Architect   US $150k+ · UK £80-85k (London)
   *   AQA Professional    US $105-120k (SDET to $155k) · UK ~£60k
   *   QA Analyst          US $60-95k · UK £35-45k
   * Pharma and financial services pay ~25% above average — most of the verticals
   * this page sells into.
   *
   * The two multipliers that turn salary into cost:
   *   - Employment on-costs. BLS March 2026: wages are 69.9% of total employer
   *     cost in private industry, so fully loaded is ~1.43x base. UK is ~1.17x —
   *     employer NI 15% above a £5,000 threshold from April 2026, plus 3%
   *     auto-enrolment pension.
   *   - Recruitment. Contingency 20-30% of first-year base; retained search
   *     25-35% of first-year total comp. Four hires, so this is a real
   *     first-year line.
   * Worked: US ~$760-790k/yr loaded, ~$900k in year one. UK ~£305k/yr, ~£365k
   * in year one.
   *
   * RECOMMENDATION: do not publish currency. It invites argument (a prospect
   * paying less discounts the whole table), the page serves three currencies so
   * one number is wrong in two of them, and it dates. The structural form makes
   * the same point and needs no maintenance: four salaries, plus on-costs of
   * ~40% in the US and ~17% in the UK, plus one-off recruitment of 20-30% of
   * first-year pay per hire.
   *
 * **§6 AND §8 ARE COUPLED.** The comparison's right column says "Monthly fee,
 * no employment cost", and §8 now carries no price at all by decision. So both
 * sides are qualitative, which is consistent. Putting figures on the in-house
 * side alone would break that — a CFO reads a quantified alternative next to an
 * unquantified us as "they won't say". If a number ever goes into one column,
 * it has to go into both.
 */


/**
 * §8. NO STARTING-FROM FIGURE, BY DECISION — the client does not want a price
 * on the page. The section answers "how would you work out my number" instead.
 *
 * The heading changed from "Starting from", which promised a figure that was
 * never coming. A heading that promises nothing is better than one that
 * promises and does not deliver.
 *
 * The three driver LABELS are approved copy: `estate` and `scope` are almost
 * verbatim from homepage FAQ 6 ("Price depends on the size of your estate and
 * how much of it is in scope"); `roles` is §3 of this page ("Not everyone below
 * is on every engagement" — "below" dropped, there is no list beside it here).
 * The one-line bodies under each are NEW COPY pending sign-off.
 *
 * **THIS CONTRADICTS HOMEPAGE FAQ 6**, which tells visitors "every service page
 * carries a starting-from figure so you can size it before you talk to us".
 * None does, and after this decision none will. That sentence needs to go or
 * change — it is client copy, so it has not been touched here. See CLAUDE.md,
 * "Two homepage FAQ answers contradict the rest of the site".
 */
export const pricing = {
  heading: "How pricing works",
  body: "Priced per engagement, with the roles included. Monthly, rolling, no minimum term.",
  driversLabel: "What sets the number",
  drivers: [
    {
      number: "01",
      label: "Which roles are on the engagement",
      body: "Not everyone is on every engagement. You pay for the roles you agreed.",
    },
    {
      number: "02",
      label: "The size of your estate",
      body: "How much software there is to own, and how much of it is under test today.",
    },
    {
      number: "03",
      label: "How much of it is in scope",
      body: "The whole quality function, or one part of it while you keep the rest.",
    },
  ],
} as const;

export const related = {
  heading: "Related",
  items: [
    {
      label: "QA Consulting & Modernization",
      href: "/services/qa-consulting-modernization",
      note: "if you're not yet sure what's wrong",
    },
    {
      label: "Test Architecture & QA Platform Engineering",
      href: "/services/qa-architecture-engineering",
      note: "when the framework is the constraint",
    },
    { label: "AI Maturity Model", href: "/services/ai-driven-validation#maturity-model-heading", note: "how we measure where you are" },
    {
      label: "AI-Native Delivery Framework",
      href: "/services/ai-driven-validation#framework-heading",
      note: "the operating model behind the function",
    },
  ],
} as const;
